<template>
  <div class="ShowParametrics">
    <a id="ShowParametrics" />
    <table class="parametricsTable border-t border-border-subtle">
      <tbody>
        <template v-for="group in combinedList" :key="group.groupCode">
          <tr v-if="group.groupName !== ''"><td colspan="2" class="type-tagline-xs">{{ group.groupName }}</td></tr>
          <template v-for="row in group.values" :key="row.code">
            <tr v-if="row.value">
              <td class="type-headline-sm">{{ row.name }}</td>
              <td class="type-sm text-text-subtle">
                <div v-html="row.value"/>
                <div v-if="design === 'b2cLight' && row.description && row.description !== ''" class="inline-block ml-8 relative top-2">
                  <Tooltip :text="row.description" :show-on-hover="true">
                    <img
                      src="/icons/info.svg"
                      class="w-16 h-16"
                      alt=""
                    >
                  </Tooltip>
                </div>
              </td>
            </tr>
          </template>
        </template>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import type  { ProductVariantModel } from '#models/productVariant';
import type { ProductModel } from '#models/product';
import type { ParametricData } from '#constants/types/algolia';
import Tooltip from '~/components/body/Tooltip.vue';
import { useGlobalContentStore } from '~/store/globalContent';

interface Props {
  productItem: ProductModel;
  activeVariant: ProductVariantModel,
  design?: 'standard' | 'b2cLight'
}

const props = withDefaults(defineProps<Props>(), {
  design: 'standard',
});

const includedGroupCodes = ['Design', 'Facts', 'Material', 'General', 'Dimensions', 'ImpDimensions', 'PackagingDimensions', 'Installation'];
// Used for PDF generation (kahrs-api ProductPdfService), not meant to show in the PDP facts table
const excludedCodes = ['CollectionDescription', 'ShortVariantDescription'];

type SortGroup = {
  groupCode: string;
  values: string[];
}
const globalContent = useGlobalContentStore();

const isUS = globalContent.getCountryCode === 'us';
const useImperial = globalContent.getMeasurement === 'imperial';
const warrantyField = isUS ? 'ProductWarrantyUS' : 'ProductWarranty';

// Fallback
let sortOrder = [
  { groupCode: 'Design', values:  [] },
  { groupCode: 'Facts', values:  [] },
  { groupCode: 'Material', values:  [] },
  { groupCode: 'General', values:  [] },
  { groupCode: 'Dimensions', values:  [] },
  { groupCode: 'PackagingDimensions', values:  [] },
  { groupCode: 'Installation', values:  [] },
] as SortGroup[];

if (props.productItem.lastChildCategory?.code === 'Wood') {
  sortOrder = [
    { groupCode: 'Dimensions', values: [ 'ItemWidth', 'ItemLength', 'ItemThickness', 'WearLayerThickness', 'ItemPackageWeight' ] },
  ];
  if (useImperial) {
    sortOrder.push({ groupCode: 'ImpDimensions', values: [ 'ItemWidthImperial', 'ItemLengthImperial', 'ItemThicknessImperial', 'WearLayerThicknessImperial', 'ItemPackageWeightImperial' ] });
  }
  sortOrder.push(
    { groupCode: 'Design', values: [ 'ProductDesign', 'ItemSurfaceTreatment', 'ProductDesignFeatures', 'ProductColourCode', 'ProductWoodSpecies', 'ProductGrading' ] },
    { groupCode: 'Material', values: [ 'ProductColourChange', 'ProductDetailDescription' ] },
    { groupCode: 'Facts', values: [ 'ProductCollectionDynamic', 'ProductFloorHeatingOption', 'ProductInstallationMethod', 'ProductJoint', 'ProductConstruction', warrantyField, 'ProductBrinellValue', 'ProductCoreMaterial' ] }
  );
}

if (props.productItem.lastChildCategory?.code === 'Vinyl') {
  sortOrder = [
    { groupCode: 'Dimensions', values: [ 'ItemWidth', 'ItemLength', 'ItemThickness', 'WearLayerThickness', 'ItemPackageWeight' ] },
  ];
  if (useImperial) {
    sortOrder.push({ groupCode: 'ImpDimensions', values: [ 'ItemWidthImperial', 'ItemLengthImperial', 'ItemThicknessImperial', 'WearLayerThicknessImperial', 'ItemPackageWeightImperial' ] });
  }
  sortOrder.push(
    { groupCode: 'Design', values: [ 'ProductDesign', 'ProductDesignFeatures', 'ProductColourCode' ] },
    { groupCode: 'Material', values: [ 'ProductColourChange', 'ProductDetailDescription' ] },
    { groupCode: 'Facts', values: [ 'ProductCollectionDynamic', 'ProductFloorHeatingOption', 'ProductInstallationMethod', 'ProductJoint', 'ProductConstruction', warrantyField ] }
  );
}
if (props.productItem.lastChildCategory?.code === 'Mouldings') {
  sortOrder = [
    { groupCode: 'Dimensions', values: [ 'ItemWidth', 'ItemLength', 'ItemHeight' ] },
  ];
  if (useImperial) {
    sortOrder.push({ groupCode: 'ImpDimensions', values: [ 'ItemWidthImperial', 'ItemLengthImperial', 'ItemHeightImperial' ] });
  }
  sortOrder.push(
    { groupCode: 'Design', values: [ 'ItemSurfaceTreatment', 'ItemWoodSpecies', 'ItemMetal', 'ProductColourCode', 'ItemSurfaceColour', 'ProductColourChange', 'ProductDetailDescription' ] },
    { groupCode: 'Facts', values: [ 'ProductCollectionDynamic', 'ProductMouldingsSubType', 'ProductMouldingsType' ] }
  );
}
if (props.productItem.lastChildCategory?.code === 'Skirtings') {
  sortOrder = [
    { groupCode: 'Dimensions', values: ['ItemWidth', 'ItemLength', 'ItemHeight'] },
  ];
  if (useImperial) {
    sortOrder.push({ groupCode: 'ImpDimensions', values: ['ItemWidthImperial', 'ItemLengthImperial', 'ItemHeightImperial'] });
  }
  sortOrder.push(
    { groupCode: 'Design', values: ['ItemSurfaceTreatment', 'ItemWoodSpecies', 'ProductColourCode', 'ItemSurfaceColour', 'ProductColourChange', 'ProductDetailDescription'] },
    { groupCode: 'Facts', values: ['ProductCollectionDynamic', 'ProductSkirtingsSubType', 'ProductSkirtingsType'] }
  );
}

if (props.productItem.lastChildCategory?.code === 'Maintenance' || props.productItem.lastChildCategory?.code === 'Installation') {
  sortOrder = [
    { groupCode: 'PackagingDimensions', values: ['ItemWidth', 'ItemLength', 'ItemThickness', 'Volume'] },
    { groupCode: 'Facts', values: ['ProductCollectionDynamic', 'ItemSurfaceColour', 'ProductAccessorySubType', 'ProductAccessoryType'] },
  ];
}

const combinedList = computed(()=> {
  let output = props.productItem._ProductRaw.parametrics;
  // Adding version parametrics
  props.activeVariant.parametrics.forEach((variantParametric) => {
    const exist = output.findIndex(c => c.code === variantParametric.code);
    if (exist > -1) {
      output[exist] = variantParametric;
    } else {
      output.push(variantParametric);
    }
  });
  // Add defining parametrics
  props.activeVariant.definingParametrics.forEach((variantParametric) => {
    const exist = output.findIndex(c => c.code === variantParametric.code);
    if (exist > -1) {
      output[exist] = variantParametric;
    } else {
      output.push(variantParametric);
    }
  });

  output = output.filter(f => includedGroupCodes.includes(f.groupCode) && !excludedCodes.includes(f.code) && (isUS || f.code !== 'ProductWarrantyUS')).map(m => {
    let value = null;
    let tooltip = null;

    if (m.type === 'list' && m.listValue?.name) {
      value = m.listValue.name;
      tooltip = m.listValue.description;
    }

    else if (m.type === 'multiple' && m.multipleValues?.length) {
      value = m.multipleValues.map(n => n.name).join(', ');
      tooltip = m.multipleValues.map(n => n.description).filter(f => f !== '').join('<br><br>');
    }

    else if ((m.type === 'decimal' || m.type === 'integer') && m.value) {
      value = m.value + '';
    }

    else if (m.value) {
      value = m.value;
    }

    if (m.uom !== '' && value) {
      value += ` ${m.uom}`;
    }

    return {
      name: m.name,
      groupCode: m.groupCode,
      groupName: m.groupName,
      value,
      code: m.code,
      description: tooltip,
    };
  });

  output = output.filter(f => f.value !== '' && f.value !== null);

  let sortedOutput = [] as {groupCode: string, groupName: string, values:ParametricData[]}[];

  // Split into groups
  output.forEach(f => {
    const exists = sortedOutput.findIndex(fi => fi.groupCode === f.groupCode);
    if (exists > -1) {
      sortedOutput[exists].values.push(f);
    } else {
      sortedOutput.push({
        groupCode: f.groupCode,
        groupName: f.groupName || '',
        values: [ f ],
      });
    }
  });

  // Filter out groups that are not in sortOrder
  sortedOutput = sortedOutput.filter(group =>
    sortOrder.some(order => order.groupCode === group.groupCode)
  );

  // Sort groups according to sortOrder
  sortedOutput.sort((a, b) => {
    const aIndex = sortOrder.findIndex(s => s.groupCode === a.groupCode);
    const bIndex = sortOrder.findIndex(s => s.groupCode === b.groupCode);
    if (aIndex === -1 && bIndex === -1) {
      return 0;
    }
    if (aIndex === -1) {
      return 1;
    }
    if (bIndex === -1) {
      return -1;
    }
    return aIndex - bIndex;
  });

  // Sort values within each group according to sortOrder
  sortedOutput.forEach(group => {
    const orderValues = sortOrder.find(s => s.groupCode === group.groupCode)?.values;
    if (orderValues) {
      group.values.sort((a, b) => {
        const aIndex = orderValues.indexOf(a.code);
        const bIndex = orderValues.indexOf(b.code);
        if (aIndex === -1 && bIndex === -1) {
          return 0;
        }
        if (aIndex === -1) {
          return 1;
        }
        if (bIndex === -1) {
          return -1;
        }
        return aIndex - bIndex;
      });
    }
  });

  return sortedOutput;
});

</script>

<style scoped lang="postcss">
.parametricsTable {
  tr {
    @apply border-b border-border-subtle last:border-none;
  }
  td {
    @apply py-12 pr-24 last:pr-0;
  }
}
</style>
